import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

// Generic helper to follow Learn REST paging (results + paging.nextPage)
const fetchPagedResults = async (platformUrl, initialPath, headers) => {
    let results = [];
    let nextPath = initialPath;

    while (nextPath) {
        const resp = await axios.get(`${platformUrl}${nextPath}`, { headers });
        const data = resp.data || {};

        if (Array.isArray(data.results)) {
            results = results.concat(data.results);
        }

        const paging = data.paging || {};
        if (paging.nextPage) {
            // nextPage is usually a relative path like "/learn/api/public/..."
            nextPath = paging.nextPage;
        } else {
            nextPath = null;
        }
    }

    return results;
};

const parseNumeric = (value) => {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
};

/**
 * Find the "Regulatory Grade" column by name (case-insensitive).
 * We do NOT use externalId at all.
 */
const findRegColumnByName = async (platformUrl, courseId, headers) => {
    const allColumns = await fetchPagedResults(
        platformUrl,
        `/learn/api/public/v2/courses/${courseId}/gradebook/columns?limit=200`,
        headers
    );

    const wantedName = "regulatory grade";

    const match =
        allColumns.find(
            (c) =>
                c &&
                typeof c.name === "string" &&
                c.name.trim().toLowerCase() === wantedName
        ) || null;

    return match;
};

const ComputeRegulatoryGrade = ({ platformUrl }) => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const courseUuid = params.get("courseUuid");

    const [courseInfo, setCourseInfo] = useState(null);
    const [termInfo, setTermInfo] = useState(null); // no longer used for program, kept for compatibility
    const [termType, setTermType] = useState(null); // 'L','M','D','E' from courseId pattern, or null
    const [manualTermType, setManualTermType] = useState("B"); // for no-pattern case

    const [overallColumn, setOverallColumn] = useState(null); // Final Grade column
    const [regColumn, setRegColumn] = useState(null);

    // Base student data; regulatory grade & scoreToPost are derived from this.
    const [students, setStudents] = useState([]);

    // Schema-related state
    const [schemaSymbols, setSchemaSymbols] = useState([]); // raw schema symbols from schema
    const [columnPossible, setColumnPossible] = useState(null); // possible for Regulatory Grade column (always 100 when created)

    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState(null); // load-time error
    const [postError, setPostError] = useState(null); // post-time error
    const [postResult, setPostResult] = useState(null);

    // Institution role flag
    const [hasRegGradeAdmin, setHasRegGradeAdmin] = useState(false);

    // Effective "Program": use termType from courseId if available, otherwise manualTermType
    const effectiveRegime = useMemo(() => {
        if (termType) return termType;
        return manualTermType || "B";
    }, [termType, manualTermType]);

    // Sorting config
    const [sortConfig, setSortConfig] = useState({
        key: "displayName",
        direction: "asc",
    });

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key, direction: "asc" };
        });
    };

    const renderSortIndicator = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === "asc" ? " ▲" : " ▼";
    };

    // Core loader so we can reuse it on mount and after posting
    const fetchData = async (clearPostResult = true) => {
        setLoading(true);
        setError(null);
        if (clearPostResult) {
            setPostResult(null);
            setPostError(null);
        }

        try {
            if (!token || !courseUuid) {
                throw new Error(
                    "Missing access token or course UUID. Please try again."
                );
            }

            const headers = { Authorization: `Bearer ${token}` };

            // 0. Check current user institution roles for REG_GRADE_ADMIN
            try {
                const meResp = await axios.get(
                    `${platformUrl}/learn/api/public/v1/users/me?expand=institutionRoleIds`,
                    { headers }
                );
                const me = meResp.data || {};
                const instRoles = me.institutionRoleIds;

                let isRegAdmin = false;
                if (Array.isArray(instRoles)) {
                    isRegAdmin = instRoles.some((r) => {
                        if (typeof r === "string") {
                            return r === "REG_GRADE_ADMIN";
                        }
                        if (r && typeof r === "object") {
                            return (
                                r.id === "REG_GRADE_ADMIN" ||
                                r.name === "REG_GRADE_ADMIN"
                            );
                        }
                        return false;
                    });
                }
                setHasRegGradeAdmin(isRegAdmin);
            } catch (roleErr) {
                console.warn(
                    "[ComputeRegulatoryGrade] Unable to fetch current user institution roles:",
                    roleErr
                );
                // If we can't read roles, leave hasRegGradeAdmin = false (tool still works)
                setHasRegGradeAdmin(false);
            }

            // 1. Get course details so we can grab the internal course PK1 (id)
            const courseResp = await axios.get(
                `${platformUrl}/learn/api/public/v3/courses/uuid:${courseUuid}`,
                { headers }
            );
            const course = courseResp.data;
            const courseId = course.id; // internal PK1
            const courseBatchUid = course.externalId || "";
            setCourseInfo(course);

            // Derive program from courseId pattern:
            // e.g., "AE26PRIMAVERA_AMING0830907_P26MACV2"
            //   → split("_") → ["AE26PRIMAVERA","AMING0830907","P26MACV2"]
            //   → second token "AMING0830907" → second char 'M'
            setTermInfo(null);
            setTermType(null);
            try {
                const courseIdStr = course.courseId || "";
                let derivedType = null;

                const parts = courseIdStr.split("_");
                if (parts.length >= 2) {
                    const secondToken = parts[1] || "";
                    if (secondToken.length >= 2) {
                        const secondChar = secondToken[1].toUpperCase();
                        if (
                            secondChar === "L" ||
                            secondChar === "M" ||
                            secondChar === "D" ||
                            secondChar === "E"
                        ) {
                            derivedType = secondChar;
                        }
                    }
                }

                setTermType(derivedType);
            } catch (programErr) {
                console.warn(
                    "[ComputeRegulatoryGrade] Could not derive program from courseId:",
                    programErr
                );
                setTermType(null);
            }

            // 3. Get the Final Grade column (special endpoint)
            const finalColResp = await axios.get(
                `${platformUrl}/learn/api/public/v2/courses/${courseId}/gradebook/columns/finalGrade`,
                { headers }
            );
            const finalGradeColumn = finalColResp.data;
            setOverallColumn(finalGradeColumn);

            // 4. Find the Regulatory Grade column by name (handles copied courses)
            const existingRegCol = await findRegColumnByName(
                platformUrl,
                courseId,
                headers
            );
            setRegColumn(existingRegCol || null);

            // 5. Compute the "possible" we should use when posting to the Regulatory column
            //    If the column exists, use its possible.
            //    If not, default to 100 (since we always create it with 100).
            let columnPossibleLocal = null;
            if (
                existingRegCol &&
                existingRegCol.score &&
                existingRegCol.score.possible !== undefined &&
                existingRegCol.score.possible !== null
            ) {
                columnPossibleLocal = parseNumeric(
                    existingRegCol.score.possible
                );
            }
            if (columnPossibleLocal === null || Number.isNaN(columnPossibleLocal)) {
                columnPossibleLocal = 100;
            }
            setColumnPossible(columnPossibleLocal);

            // 6. Get course memberships (students only) via Membership API, paged, expand=user
            const memberships = await fetchPagedResults(
                platformUrl,
                `/learn/api/public/v1/courses/${courseId}/users?role=Student&limit=200&expand=user`,
                headers
            );

            // 7. Get ALL posted final grade user grades (paged)
            const grades = await fetchPagedResults(
                platformUrl,
                `/learn/api/public/v2/courses/${courseId}/gradebook/columns/finalGrade/users?limit=200`,
                headers
            );

            // 8. Get final grades including unposted grades (paged)
            const unpostedGrades = await fetchPagedResults(
                platformUrl,
                `/learn/api/public/v2/courses/${courseId}/gradebook/columns/finalGrade/users?includeUnpostedGrades=true&limit=200`,
                headers
            );

            // 9. Fetch existing Regulatory Grade user grades (if column exists)
            let regGrades = [];
            if (existingRegCol && existingRegCol.id) {
                regGrades = await fetchPagedResults(
                    platformUrl,
                    `/learn/api/public/v2/courses/${courseId}/gradebook/columns/${existingRegCol.id}/users?limit=200`,
                    headers
                );
            }

            // 10. Load the grade schema for the Final Grade column (we trust the schema symbols)
            let schemaSymbolsLocal = [];
            const finalSchemaId =
                finalGradeColumn.grading && finalGradeColumn.grading.schemaId;

            if (finalSchemaId) {
                try {
                    const schemaResp = await axios.get(
                        `${platformUrl}/learn/api/public/v1/courses/${courseId}/gradebook/schemas/${finalSchemaId}`,
                        { headers }
                    );
                    const schema = schemaResp.data;
                    schemaSymbolsLocal = Array.isArray(schema.symbols)
                        ? schema.symbols
                        : [];
                } catch (schemaErr) {
                    console.warn(
                        "[ComputeRegulatoryGrade] Failed to fetch grade schema; cannot remap scores precisely:",
                        schemaErr
                    );
                }
            }

            setSchemaSymbols(schemaSymbolsLocal);

            // Map posted final grades by userId for quick lookup
            const gradeMap = {};
            grades.forEach((g) => {
                if (g && g.userId) {
                    gradeMap[g.userId] = g;
                }
            });

            // Map unposted final grades (includeUnpostedGrades) by userId
            const unpostedGradeMap = {};
            unpostedGrades.forEach((g) => {
                if (g && g.userId) {
                    unpostedGradeMap[g.userId] = g;
                }
            });

            // Map existing regulatory grades by userId
            const regGradeMap = {};
            regGrades.forEach((g) => {
                if (g && g.userId) {
                    regGradeMap[g.userId] = g;
                }
            });

            // 11. Build base student rows with hydrated name & core grade info
            const rows = memberships.map((m) => {
                const user = m.user || {};
                const nameObj = user.name || {};
                const fullName = `${nameObj.given || ""} ${
                    nameObj.family || ""
                }`.trim();
                const displayName = fullName || user.userName || m.userId;
                const username = user.userName || "";
                const userBatchUid = user.externalId || "";

                const grade = gradeMap[m.userId];
                const unpostedGrade = unpostedGradeMap[m.userId];

                // --- POSTED FINAL GRADE SCHEMA VALUE + ORIGINAL POINTS ---
                let schemaDisplay = "-";
                let schemaNumeric = null;
                let originalPoints = null; // numeric score we normally would have

                if (grade && grade.displayGrade) {
                    const dg = grade.displayGrade;
                    if (dg.text !== undefined && dg.text !== null) {
                        schemaDisplay = String(dg.text); // "3", "7", or "3.00", etc.
                        schemaNumeric = parseNumeric(schemaDisplay);
                    }
                    if (dg.score !== undefined && dg.score !== null) {
                        originalPoints = parseNumeric(dg.score);
                    }
                }

                // Fallbacks if displayGrade.* was missing for some reason
                if (grade && schemaDisplay === "-") {
                    if (
                        grade.schemaValue !== undefined &&
                        grade.schemaValue !== null
                    ) {
                        schemaDisplay = String(grade.schemaValue);
                        schemaNumeric = parseNumeric(grade.schemaValue);
                    } else if (
                        grade.score !== undefined &&
                        grade.score !== null
                    ) {
                        schemaDisplay = String(grade.score);
                        schemaNumeric = parseNumeric(grade.score);
                    } else if (
                        grade.text !== undefined &&
                        grade.text !== null
                    ) {
                        schemaDisplay = String(grade.text);
                    }
                }

                if (grade && originalPoints === null) {
                    if (
                        grade.score !== undefined &&
                        grade.score !== null
                    ) {
                        originalPoints = parseNumeric(grade.score);
                    }
                }

                // --- UNPOSTED FINAL GRADE (includeUnpostedGrades) ---
                let unpostedDisplay = "-";
                let unpostedSchemaNumeric = null;
                let unpostedOriginalPoints = null;

                if (unpostedGrade && unpostedGrade.displayGrade) {
                    const dg = unpostedGrade.displayGrade;
                    if (dg.text !== undefined && dg.text !== null) {
                        unpostedDisplay = String(dg.text);
                        unpostedSchemaNumeric = parseNumeric(unpostedDisplay);
                    }
                    if (dg.score !== undefined && dg.score !== null) {
                        unpostedOriginalPoints = parseNumeric(dg.score);
                    }
                }

                if (unpostedGrade && unpostedDisplay === "-") {
                    if (
                        unpostedGrade.schemaValue !== undefined &&
                        unpostedGrade.schemaValue !== null
                    ) {
                        unpostedDisplay = String(unpostedGrade.schemaValue);
                        unpostedSchemaNumeric = parseNumeric(
                            unpostedGrade.schemaValue
                        );
                    } else if (
                        unpostedGrade.score !== undefined &&
                        unpostedGrade.score !== null
                    ) {
                        unpostedDisplay = String(unpostedGrade.score);
                        unpostedSchemaNumeric = parseNumeric(
                            unpostedGrade.score
                        );
                    } else if (
                        unpostedGrade.text !== undefined &&
                        unpostedGrade.text !== null
                    ) {
                        unpostedDisplay = String(unpostedGrade.text);
                    }
                }

                if (unpostedGrade && unpostedOriginalPoints === null) {
                    if (
                        unpostedGrade.score !== undefined &&
                        unpostedGrade.score !== null
                    ) {
                        unpostedOriginalPoints = parseNumeric(
                            unpostedGrade.score
                        );
                    }
                }

                // --- EXISTING REGULATORY GRADE (if any) ---
                let existingRegDisplay = "-";
                let existingRegScore = null;

                const existing = regGradeMap[m.userId];
                if (existing) {
                    if (existing.displayGrade) {
                        const dg = existing.displayGrade;
                        if (
                            dg.text !== undefined &&
                            dg.text !== null
                        ) {
                            existingRegDisplay = String(dg.text);
                        }
                        if (
                            dg.score !== undefined &&
                            dg.score !== null
                        ) {
                            existingRegScore = parseNumeric(dg.score);
                        }
                    }
                    if (existingRegScore === null) {
                        if (
                            existing.score !== undefined &&
                            existing.score !== null
                        ) {
                            existingRegScore = parseNumeric(existing.score);
                        }
                    }

                    // If we have a score but not a schema label, try to infer it by band
                    if (
                        existingRegDisplay === "-" &&
                        existingRegScore !== null &&
                        columnPossibleLocal &&
                        schemaSymbolsLocal.length > 0
                    ) {
                        const pct =
                            (existingRegScore / columnPossibleLocal) * 100.0;
                        const match = schemaSymbolsLocal.find((sym) => {
                            const low = parseNumeric(sym.lowerBound);
                            const high = parseNumeric(sym.upperBound);
                            if (low === null || high === null) return false;
                            return pct >= low && pct <= high;
                        });
                        if (match && match.text !== undefined) {
                            existingRegDisplay = String(match.text);
                        }
                    }
                }

                return {
                    userId: m.userId,
                    username,
                    displayName,
                    courseRoleId: m.courseRoleId,
                    overallDisplay: schemaDisplay, // posted final schema label
                    schemaNumeric,
                    originalPoints,
                    unpostedDisplay,          // unposted final schema label
                    unpostedSchemaNumeric,
                    unpostedOriginalPoints,
                    existingRegDisplay,
                    existingRegScore,
                    courseBatchUid,
                    userBatchUid,
                };
            });

            setStudents(rows);
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "An error occurred while loading data."
            );
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchData(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Compute regulatory grade & scoreToPost from base students + courseId-based program rules
    const computedStudents = useMemo(() => {
        return students.map((s) => {
            let regulatorySchemaNumeric = null;
            let regulatorySchemaDisplay = "-";
            let floorApplied = false;

            if (s.schemaNumeric !== null && s.schemaNumeric !== undefined) {
                const val = s.schemaNumeric;
                let floorThreshold = null;

                // Current mapping (after swap):
                //   L: < 5.00 -> 5.00
                //   M/D/E: < 7.00 -> 5.00
                if (effectiveRegime === "L") {
                    floorThreshold = 5.0;
                } else if (
                    effectiveRegime === "M" ||
                    effectiveRegime === "D" ||
                    effectiveRegime === "E"
                ) {
                    floorThreshold = 7.0;
                }

                if (floorThreshold !== null && val < floorThreshold) {
                    floorApplied = true;
                    regulatorySchemaNumeric = 5.0;
                    regulatorySchemaDisplay = "5.00";
                } else {
                    regulatorySchemaNumeric = val;
                    regulatorySchemaDisplay = s.overallDisplay;
                }
            }

            let scoreToPost = null;

            if (
                regulatorySchemaNumeric !== null &&
                s.schemaNumeric !== null &&
                s.schemaNumeric !== undefined
            ) {
                // For scores above the floor: use the posted final grade schema value.
                // For scores below the floor: use the floored value (5.00).
                const labelToLookup = floorApplied
                    ? regulatorySchemaNumeric
                    : s.schemaNumeric;

                let pct = null;

                if (
                    columnPossible !== null &&
                    columnPossible !== undefined &&
                    schemaSymbols &&
                    schemaSymbols.length > 0
                ) {
                    const sym = schemaSymbols.find((sym) => {
                        const labelNum = parseNumeric(sym.text);
                        return (
                            labelNum !== null && labelNum === labelToLookup
                        );
                    });
                    if (sym) {
                        pct = parseNumeric(sym.absoluteValue);
                    }
                }

                if (pct !== null && !Number.isNaN(pct)) {
                    // absoluteValue is treated as a percentage (0–100) of the Regulatory column (possible=100)
                    scoreToPost = (pct / 100.0) * columnPossible;
                } else if (
                    // Fallback: if we cannot resolve schema→absolute for some reason
                    !floorApplied &&
                    s.originalPoints !== null &&
                    !Number.isNaN(s.originalPoints)
                ) {
                    scoreToPost = s.originalPoints;
                }
            }

            return {
                ...s,
                regulatoryGradeNumeric: regulatorySchemaNumeric,
                regulatoryGradeDisplay: regulatorySchemaDisplay,
                scoreToPost,
            };
        });
    }, [students, effectiveRegime, schemaSymbols, columnPossible]);

    // Sorting
    const sortedStudents = useMemo(() => {
        const sortable = [...computedStudents];
        const { key, direction } = sortConfig;
        const factor = direction === "asc" ? 1 : -1;

        sortable.sort((a, b) => {
            const valA = a[key];
            const valB = b[key];

            if (valA == null && valB == null) return 0;
            if (valA == null) return 1;
            if (valB == null) return -1;

            const numA =
                typeof valA === "number" ? valA : Number(valA);
            const numB =
                typeof valB === "number" ? valB : Number(valB);
            const bothNumeric =
                !Number.isNaN(numA) && !Number.isNaN(numB);

            if (bothNumeric) {
                if (numA < numB) return -1 * factor;
                if (numA > numB) return 1 * factor;
                return 0;
            }

            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();

            if (strA < strB) return -1 * factor;
            if (strA > strB) return 1 * factor;
            return 0;
        });

        return sortable;
    }, [computedStudents, sortConfig]);

    const handlePostToBlackboard = async () => {
        if (!courseInfo || !overallColumn) {
            return;
        }

        setPosting(true);
        setPostError(null);
        setPostResult(null);

        try {
            const headers = { Authorization: `Bearer ${token}` };
            const courseId = courseInfo.id;

            let regCol = regColumn;

            // Extra safety: if we don't have it in state, try to look it up again by name
            if (!regCol) {
                regCol = await findRegColumnByName(platformUrl, courseId, headers);
                if (regCol) {
                    setRegColumn(regCol);
                }
            }

            // Create the "Regulatory Grade" column only if it truly does not exist
            if (!regCol) {
                const createBody = {
                    name: "Regulatory Grade",
                    description:
                        "Regulatory grade based on Program rules; score matches the mapped schema value.",
                    score: {
                        // ALWAYS 100 possible for Regulatory Grade
                        possible: 100,
                    },
                    availability: {
                        // Make visible to students
                        available: "Yes",
                    },
                    grading: {
                        type: "Manual",
                        // try to reuse same schema if Final Grade has one
                        schemaId:
                            overallColumn.grading &&
                            overallColumn.grading.schemaId
                                ? overallColumn.grading.schemaId
                                : undefined,
                    },
                };

                const createResp = await axios.post(
                    `${platformUrl}/learn/api/public/v2/courses/${courseId}/gradebook/columns`,
                    createBody,
                    { headers }
                );

                regCol = createResp.data;
                setRegColumn(regCol);
                // Ensure local possible matches the new column (100)
                setColumnPossible(100);
            }

            // 2. Post grades for all students where we computed a scoreToPost
            const updatable = computedStudents.filter(
                (s) => s.scoreToPost !== null && !Number.isNaN(s.scoreToPost)
            );

            for (const s of updatable) {
                const body = {
                    score: s.scoreToPost,
                };

                await axios.patch(
                    `${platformUrl}/learn/api/public/v2/courses/${courseId}/gradebook/columns/${regCol.id}/users/${s.userId}`,
                    body,
                    { headers }
                );
            }

            setPostResult(
                `Successfully posted ${updatable.length} regulatory grades to "${regCol.name}".`
            );

            // Refresh so "Current Regulatory Grade" reflects the posted values
            await fetchData(false);
        } catch (err) {
            console.error(err);
            setPostError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to post grades to Blackboard."
            );
        } finally {
            setPosting(false);
        }
    };

    if (loading) {
        return (
            <div className="bb-container">
                <p>Loading course and grade data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bb-container bb-error">
                <p>{error}</p>
                <p>
                    Please check the token, course permissions, or API configuration
                    and try again.
                </p>
            </div>
        );
    }

    return (
        <div className="bb-container">
            {/* Messages at the top */}
            {postError && (
                <div
                    className="bb-message bb-message-error"
                    style={{ marginBottom: "0.75rem", color: "red" }}
                >
                    {postError}
                </div>
            )}
            {postResult && (
                <div
                    className="bb-message bb-message-success"
                    style={{ marginBottom: "0.75rem", color: "green" }}
                >
                    {postResult}
                </div>
            )}

            {/* Course summary + top-right button */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "0.75rem",
                    gap: "1rem",
                }}
            >
                <div className="bb-course-summary">
                    {courseInfo && (
                        <>
                            <p style={{ margin: 0 }}>
                                <strong>Course:</strong> {courseInfo.name} (
                                {courseInfo.courseId})
                            </p>
                            {courseInfo.externalId && hasRegGradeAdmin && (
                                <p style={{ margin: 0 }}>
                                    <strong>Course BatchUid:</strong>{" "}
                                    {courseInfo.externalId}
                                </p>
                            )}
                            {overallColumn && (
                                <p style={{ margin: 0 }}>
                                    <strong>Final Grade Column:</strong>{" "}
                                    {overallColumn.name}
                                </p>
                            )}
                        </>
                    )}

                    {/* Program display based on courseId, with manual override fallback */}
                    {termType ? (
                        <p style={{ margin: "0.15rem 0 0 0" }}>
                            <strong>Program (from Course ID):</strong> {termType}
                            {termType === "L" && " – < 5.00 → 5.00"}
                            {(termType === "M" ||
                                    termType === "D" ||
                                    termType === "E") &&
                                " – < 7.00 → 5.00"}
                            {termType === "B" && " – no change"}
                        </p>
                    ) : (
                        <div style={{ marginTop: "0.15rem" }}>
                            <p style={{ margin: "0 0 0.15rem 0" }}>
                                Program not recognized from Course ID. Select Program:
                            </p>
                            <select
                                value={manualTermType}
                                onChange={(e) =>
                                    setManualTermType(e.target.value)
                                }
                            >
                                <option value="L">
                                    L – schema &lt; 5.00 → 5.00
                                </option>
                                <option value="M">
                                    M – schema &lt; 7.00 → 5.00
                                </option>
                                <option value="D">
                                    D – schema &lt; 7.00 → 5.00
                                </option>
                                <option value="E">
                                    E – schema &lt; 7.00 → 5.00
                                </option>
                                <option value="B">
                                    B – no change
                                </option>
                            </select>
                        </div>
                    )}

                    {regColumn && (
                        <p style={{ margin: "0.15rem 0 0 0" }}>
                            <strong>Regulatory Grade Column:</strong>{" "}
                            {regColumn.name}
                        </p>
                    )}
                </div>

                <div>
                    <button
                        className="bb-button"
                        onClick={handlePostToBlackboard}
                        disabled={posting || students.length === 0}
                    >
                        {posting
                            ? "Posting Official Calculation in Blackboard..."
                            : "Post Official Calculation in Blackboard"}
                    </button>
                </div>
            </div>

            {/* Legend for indicators */}
            <div
                style={{
                    marginBottom: "0.35rem",
                    fontSize: "0.85rem",
                }}
            >
                <strong>Legend:</strong>{" "}
                <span
                    style={{
                        color: "#c00",
                        fontWeight: "bold",
                        marginRight: "0.25rem",
                    }}
                >
                    ⚠
                </span>
                indicates a mismatch between posted and unposted final grades, or
                between current and new regulatory grades.
            </div>

            {/* Student count message - same color as success message, not bold label */}
            <div
                style={{
                    marginBottom: "0.5rem",
                    fontSize: "0.85rem",
                    color: "green",
                }}
            >
                Students loaded: {students.length}
            </div>

            <table className="bb-table">
                <thead>
                <tr>
                    <th
                        onClick={() => handleSort("displayName")}
                        style={{ cursor: "pointer" }}
                    >
                        Student Name{renderSortIndicator("displayName")}
                    </th>
                    <th
                        onClick={() => handleSort("username")}
                        style={{ cursor: "pointer" }}
                    >
                        Username{renderSortIndicator("username")}
                    </th>
                    {hasRegGradeAdmin && (
                        <th
                            onClick={() => handleSort("userBatchUid")}
                            style={{ cursor: "pointer" }}
                        >
                            User BatchUid
                            {renderSortIndicator("userBatchUid")}
                        </th>
                    )}
                    <th
                        onClick={() => handleSort("overallDisplay")}
                        style={{ cursor: "pointer" }}
                    >
                        Posted Final Grade (Schema)
                        {renderSortIndicator("overallDisplay")}
                    </th>
                    <th
                        onClick={() => handleSort("unpostedDisplay")}
                        style={{ cursor: "pointer" }}
                    >
                        Unposted Final Grade (Schema)
                        {renderSortIndicator("unpostedDisplay")}
                    </th>
                    <th
                        onClick={() => handleSort("existingRegDisplay")}
                        style={{ cursor: "pointer" }}
                    >
                        Current Regulatory Grade
                        {renderSortIndicator("existingRegDisplay")}
                    </th>
                    <th
                        onClick={() =>
                            handleSort("regulatoryGradeDisplay")
                        }
                        style={{ cursor: "pointer" }}
                    >
                        New Regulatory Grade (Program rule)
                        {renderSortIndicator("regulatoryGradeDisplay")}
                    </th>
                </tr>
                </thead>
                <tbody>
                {sortedStudents.length === 0 && (
                    <tr>
                        <td colSpan={hasRegGradeAdmin ? 7 : 6}>
                            No student enrollments found.
                        </td>
                    </tr>
                )}
                {sortedStudents.map((s) => {
                    const postedVsUnpostedDiff =
                        s.unpostedDisplay !== "-" &&
                        s.unpostedDisplay !== s.overallDisplay;

                    const regDiff =
                        s.regulatoryGradeDisplay !== "-" &&
                        s.regulatoryGradeDisplay !== s.existingRegDisplay;

                    return (
                        <tr key={s.userId}>
                            <td>{s.displayName}</td>
                            <td>{s.username}</td>
                            {hasRegGradeAdmin && (
                                <td>{s.userBatchUid || "-"}</td>
                            )}
                            <td>{s.overallDisplay}</td>
                            <td>
                                {s.unpostedDisplay !== "-"
                                    ? s.unpostedDisplay
                                    : "-"}
                                {postedVsUnpostedDiff && (
                                    <span
                                        title="Posted and unposted final grades are different."
                                        style={{
                                            marginLeft: "0.25rem",
                                            color: "#c00",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        ⚠
                                    </span>
                                )}
                            </td>
                            <td>
                                {s.existingRegDisplay !== "-"
                                    ? s.existingRegDisplay
                                    : "-"}
                            </td>
                            <td>
                                {s.regulatoryGradeDisplay !== "-"
                                    ? s.regulatoryGradeDisplay
                                    : "-"}
                                {regDiff && (
                                    <span
                                        title="Current and new regulatory grades are different."
                                        style={{
                                            marginLeft: "0.25rem",
                                            color: "#c00",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        ⚠
                                    </span>
                                )}
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
};

export default ComputeRegulatoryGrade;